<script lang="ts">
	import { page } from '$app/state'
	import { attrs } from '#library/attrs.ts'
	import type { Blok, Header } from '#library/components.schema.build.ts'
	import Image from '#components/image.svelte'

	interface Props {
		blok?: Blok<Header>
	}

	let { blok = undefined, image = undefined }: Props = $props()

	const tag = $derived(blok?.title?.tag || 'h1')
	const crumbs = $derived(page?.params?.page?.split('/').filter(Boolean) || [])
</script>

<div
	class="header {blok?.theme || (blok?.accent ? 'theme-crocus-on-white' : 'theme-crocus-on-light-gimson')}"
	class:accent={blok?.accent}
	{...blok && attrs(blok)}
>
	<div class="contain" class:accent={blok?.accent} class:portrait={image?.filename}>
		<div>
			<div class="trail">
				<a href="/">Home</a>
				<span class="separator">/</span>
				{#if crumbs?.length}
					{#each crumbs.slice(0, -1) as crumb, i (i)}
						{@const path = '/' + crumbs.slice(0, i + 1).join('/')}
						<a href={path}>{crumb}</a>
						<span class="separator">/</span>
					{/each}
				{/if}
			</div>
			<svelte:element this={tag} class="title">
				{blok?.title?.text || page?.data?.story?.name}
			</svelte:element>
		</div>
		{#if image?.filename}
			<figure class="figure">
				<Image src={image.filename} alt={image.alt} focus={image.focus} aspect="1/1" />
			</figure>
		{/if}
	</div>
</div>

<style lang="css">
	.header {
		background-color: var(--c-background);
		box-sizing: content-box;
		color: var(--c-foreground);
		display: grid;

		&::before {
			background-image: var(--pattern);
			content: '';
			display: block;
			height: 1.5rem;
		}

		&.accent::before {
			order: 1;
		}

		+ :global(.hero::before) {
			display: none;
		}
	}

	.contain {
		padding-block: 1.75rem 1.25rem;

		@media (--from-mobile) {
			padding-block: 3rem 1.5rem;
		}

		&.accent {
			padding-block: 2rem 1rem;

			@media (--from-mobile) {
				padding-block: 3.5rem 2.5rem;
			}
		}

		&.portrait {
			display: grid;
			gap: 1rem;

			@media (--from-mobile) {
				grid-template-columns: 1fr 1fr;
			}
		}
	}

	.trail {
		display: flex;
		gap: 0.25rem;
		font: 500 var(--f-h6);
		letter-spacing: var(--ls-h6);
		text-transform: uppercase;
	}

	.title {
		font: 600 var(--f-h1);
		letter-spacing: var(--ls-h1);
		margin-block-start: 0.5rem;

		@media (--from-mobile) {
			margin-block-start: 1rem;
		}
	}
</style>
